import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaSection } from "@/components/CtaSection";
import { FaqSection } from "@/components/FaqSection";
import { Testimonials } from "@/components/Testimonials";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { COMPANY, waLink } from "@/data/company";
import { BAIRROS } from "@/data/bairros";
import { serviceUrl, comboUrl } from "@/lib/routes";
import { serviceSchema, faqSchema, breadcrumbSchema, localBusinessSchema } from "@/lib/schema";

export default function ServicoPage({ service }) {
  const url = serviceUrl(service);
  const crumbs = [
    { name: "Início", url: "/" },
    { name: service.nome, url },
  ];
  const depoimentos = BAIRROS.flatMap((b) =>
    b.depoimentos.filter((d) => d.servico === service.nome).map((d) => ({ ...d, bairro: b.nome }))
  ).slice(0, 4);

  return (
    <main className="max-w-7xl mx-auto px-4 md:px-8" data-testid="servico-page">
      <Seo
        title={`${service.nome} em Guarulhos 24h | Orçamento Grátis (11) 97195-0197`}
        description={`${service.resumo} Ligue (11) 97195-0197.`}
        path={url}
        image={service.image}
        jsonLd={[serviceSchema(service.nome, service.resumo, url), faqSchema(service.faq), breadcrumbSchema(crumbs), localBusinessSchema()]}
      />
      <Breadcrumbs items={crumbs} />

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center py-6">
        <div>
          <span className="inline-block bg-brand-red text-white text-xs font-heading font-bold uppercase px-3 py-1.5 rounded-full mb-4">Atendimento 24 horas em Guarulhos</span>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-dark uppercase tracking-tight leading-tight">
            {service.nome} em <span className="text-brand-red">Guarulhos</span>
          </h1>
          <p className="mt-4 text-gray-600 text-sm md:text-base leading-relaxed">{service.resumo}</p>
          <div className="mt-6">
            <CtaButtons servico={service} />
          </div>
        </div>
        <img src={service.image} alt={`${service.nome} em Guarulhos - equipe profissional em atendimento`} width="1264" height="848" className="rounded-2xl shadow-lg w-full" />
      </section>

      <section className="py-8 text-gray-700 text-sm md:text-base leading-relaxed space-y-4 max-w-4xl">
        {service.descricao.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </section>

      <section className="py-8" data-testid="problemas-section">
        <h2 className="font-heading font-black text-2xl md:text-3xl text-brand-dark uppercase tracking-tight mb-6">Sinais de que você precisa deste serviço</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl">
          {service.problemas.map((p, i) => (
            <div key={i} className="flex items-start gap-3 bg-brand-light rounded-lg px-4 py-3">
              <AlertTriangle size={18} className="text-brand-red shrink-0 mt-0.5" />
              <span className="text-sm md:text-base text-gray-700">{p}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="py-8" data-testid="processo-section">
        <h2 className="font-heading font-black text-2xl md:text-3xl text-brand-dark uppercase tracking-tight mb-6">Como funciona o atendimento</h2>
        <ol className="space-y-4 max-w-4xl">
          {service.processo.map((p, i) => (
            <li key={i} className="flex items-start gap-4">
              <span className="w-9 h-9 rounded-full bg-brand-red text-white font-heading font-black flex items-center justify-center shrink-0">{i + 1}</span>
              <span className="text-sm md:text-base text-gray-700 pt-1.5">{p}</span>
            </li>
          ))}
        </ol>
        <div className="flex items-center gap-2 mt-6 text-sm md:text-base font-medium text-brand-dark">
          <CheckCircle2 size={20} className="text-brand-green" /> Serviço com garantia formal e nota fiscal.
        </div>
      </section>

      <section className="py-8" data-testid="bairros-servico-section">
        <h2 className="font-heading font-black text-2xl md:text-3xl text-brand-dark uppercase tracking-tight mb-3">
          {service.nome} nos bairros de Guarulhos
        </h2>
        <p className="text-gray-600 text-sm md:text-base mb-6 max-w-3xl">
          Atendemos com equipes de plantão 24 horas em todos os bairros de Guarulhos. Veja a página exclusiva do serviço no seu bairro:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {BAIRROS.map((b) => (
            <Link key={b.slug} to={comboUrl(service, b)} className="bg-white border border-gray-100 rounded-lg px-4 py-3 shadow-sm hover:shadow-md hover:border-brand-red/40 text-sm font-medium text-brand-dark hover:text-brand-red transition-all" data-testid={`combo-link-${b.slug}`}>
              {service.nome} {b.prep} {b.nome}
            </Link>
          ))}
        </div>
      </section>

      {depoimentos.length > 0 && <Testimonials depoimentos={depoimentos} titulo={`Clientes que contrataram ${service.nome.toLowerCase()}`} />}

      <FaqSection faq={service.faq} titulo={`Dúvidas sobre ${service.nome.toLowerCase()} em Guarulhos`} />

      <CtaSection
        titulo={`Precisa ${service.verbo} agora?`}
        texto="Orçamento gratuito em minutos pelo WhatsApp. Preço fechado, garantia formal e chegada em até 40 minutos em Guarulhos."
        waMsg={`Olá! Preciso de ${service.nome.toLowerCase()} em Guarulhos. Pode me passar um orçamento?`}
      />
    </main>
  );
}

const CtaButtons = ({ servico }) => (
  <div className="flex flex-col sm:flex-row gap-3">
    <a href={waLink(`Olá! Preciso de ${servico.nome.toLowerCase()} em Guarulhos. Pode me passar um orçamento?`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenHover text-white font-heading font-bold px-7 py-3.5 rounded-full transition-transform hover:scale-105" data-testid="servico-whatsapp-btn">
      <WhatsAppIcon /> Chamar no WhatsApp
    </a>
    <a href={`tel:${COMPANY.telefoneRaw}`} className="inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redDark text-white font-heading font-bold px-7 py-3.5 rounded-full transition-transform hover:scale-105" data-testid="servico-call-btn">
      Ligar: {COMPANY.telefone}
    </a>
  </div>
);
